'use client'

import { clsx } from 'clsx'
import { type ComponentProps, type FC, useEffect, useRef } from 'react'

import { EXPERIMENTS_TRACK_EVENT_REMOVE_BANNERS_TOP_BANNER } from '../../../experiments/eventTracking'
import { useExperimentRemoveBannersTracking } from '../../../experiments/experiment-remove-banners/useExperimentRemoveBannersTracking'
import {
  AD_BANNER_AUTO_REFRESH_DATA_ATTR,
  AD_BANNER_HEIGHT_DATA_ATTR,
  AD_BANNER_PLACEHOLDER_CLASS_NAME,
  AD_BANNER_TAGS_DATA_ATTR,
  AD_BANNER_WIDTH_DATA_ATTR,
  type AdServerAdBannerDimensions,
} from '../../adServerConst'
import {
  adServerInitCommandIsPushed,
  adServerSlotsPushLoadCommand,
} from '../../adServerSlots'
import { useAdServerCloseBannerControls } from '../../useAdServerCloseBannerControls'
import { useAdServerConfigurationContext } from '../AdServerConfigurationContextProvider'

import { AdServerBannerCloseButton } from './AdServerBannerCloseButton'
import { AdServerBannerLegalText } from './AdServerBannerLegalText'
import { AdServerBannerWrapper } from './AdServerBannerWrapper'

type AdSlotDimensions = {
  height: number
  width: number
}

const getBannerProps = ({
  bannerClasses,
  bannerTags,
  dimensions,
  isCarousel,
  showSkeleton,
}: {
  bannerClasses: string
  bannerTags: string[]
  dimensions: AdSlotDimensions
  isCarousel: boolean
  showSkeleton: boolean
}): Record<string, unknown> => ({
  [AD_BANNER_HEIGHT_DATA_ATTR]: dimensions.height,
  [AD_BANNER_TAGS_DATA_ATTR]: bannerTags.toString(),
  [AD_BANNER_WIDTH_DATA_ATTR]: dimensions.width,
  className: bannerClasses,
  ...(showSkeleton && {
    style: {
      aspectRatio: dimensions.width / dimensions.height,
      maxWidth: '100%',
      width: `${dimensions.width}px`,
    },
  }),
  ...(isCarousel && {
    [AD_BANNER_AUTO_REFRESH_DATA_ATTR]: 'true',
  }),
})

export type AdBannerProps = ComponentProps<'div'> & {
  bannerClasses?: string
  bannerTags: string[]
  className?: string
  classNameOnLoad?: string
  dimensions: AdServerAdBannerDimensions
  optional?: boolean

  /**
   * Reserves the space for the banner by displaying a skeleton with banner sizes
   */
  showSkeleton?: boolean
}

export const AdServerBannerContent: FC<
  AdBannerProps & { isCarousel: boolean }
> = ({
  bannerClasses: bannerClassesProp,
  bannerTags,
  className,
  classNameOnLoad,
  dimensions,
  isCarousel,
  showSkeleton = false,
  ...restProps
}) => {
  const { getBanner } = useAdServerConfigurationContext()
  const id = bannerTags.toString()
  const bannerData = getBanner({
    height: dimensions.height,
    id,
    width: dimensions.width,
  })
  const bannerRef = useRef<HTMLDivElement>(null)
  const closeBannerControls = useAdServerCloseBannerControls({
    metaDataContent: bannerData?.metadata,
  })
  const trackBannerInteraction = useExperimentRemoveBannersTracking(
    EXPERIMENTS_TRACK_EVENT_REMOVE_BANNERS_TOP_BANNER,
  )

  // For banners mounted later on the page (e.g. cart-intermediate sidebar)
  useEffect(() => {
    const slot = bannerRef.current
    if (!slot) {
      return
    }

    if (adServerInitCommandIsPushed()) {
      adServerSlotsPushLoadCommand([slot])
    }
  }, [])

  useEffect(() => {
    const handleWindowBlur = (): void => {
      const { activeElement } = document
      const isIframeFocusedInsideBanner =
        activeElement?.tagName === 'IFRAME' &&
        Boolean(bannerRef.current?.contains(activeElement)) &&
        bannerRef.current?.classList.contains('ad-server-top-header-banner')

      if (isIframeFocusedInsideBanner) {
        trackBannerInteraction()
      }
    }

    addEventListener('blur', handleWindowBlur)

    return (): void => {
      removeEventListener('blur', handleWindowBlur)
    }
  }, [trackBannerInteraction])

  if (!closeBannerControls.showBanner) {
    return null
  }

  const bannerClasses = clsx(
    AD_BANNER_PLACEHOLDER_CLASS_NAME,
    bannerClassesProp,
  )
  const bannerProps = getBannerProps({
    bannerClasses,
    bannerTags,
    dimensions,
    isCarousel,
    showSkeleton,
  })
  const legalButton = bannerData?.metadata?.customData?.legalButton

  return (
    <AdServerBannerWrapper
      bannerData={bannerData}
      bannerRef={bannerRef}
      className={clsx(
        'ad-server-banner',
        className,
        bannerData && classNameOnLoad,
      )}
      isCarousel={isCarousel}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- not for user interaction */}
      <div
        {...restProps}
        {...bannerProps}
        onClick={(): void => {
          if (bannerClasses.includes('ad-server-top-header-banner')) {
            trackBannerInteraction()
          }
        }}
        ref={bannerRef}
      >
        <div data-as-container="" />
      </div>
      {closeBannerControls.showCloseButton ? (
        <AdServerBannerCloseButton onClick={closeBannerControls.handleClose} />
      ) : null}
      {legalButton?.label && legalButton.text ? (
        <AdServerBannerLegalText
          id={id}
          label={legalButton.label}
          text={legalButton.text}
        />
      ) : null}
    </AdServerBannerWrapper>
  )
}
